// Merkezi state yonetimi (Vanilla JS state.js'in React karsiligi)
// Context API kullaniyoruz (Zustand yerine - dependency sorunu yok)
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import type { User } from '../types'

// Toast mesaj tipi
export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  duration?: number
}

interface AppState {
  // Auth state
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean

  // Theme state
  theme: 'dark' | 'light'

  // Sidebar state
  sidebarOpen: boolean

  // Toast state
  toasts: Toast[]

  // WebSocket state
  wsConnected: boolean
}

interface AppContextType extends AppState {
  // Auth actions
  setUser: (user: User | null) => void
  setIsLoading: (loading: boolean) => void

  // Theme actions
  toggleTheme: () => void

  // Sidebar actions
  setSidebarOpen: (open: boolean) => void

  // Toast actions
  addToast: (message: string, type: Toast['type'], duration?: number) => void
  removeToast: (id: string) => void

  // WebSocket actions
  setWsConnected: (connected: boolean) => void
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export function AppProvider({ children }: { children: ReactNode }) {
  // Theme state (localStorage'dan yukle)
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('theme')
    return (saved === 'dark' || saved === 'light') ? saved : 'dark'
  })

  // Sidebar state (localStorage'dan yukle)
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebarOpen')
    return saved === 'false' ? false : true
  })

  // Toast state
  const [toasts, setToasts] = useState<Toast[]>([])

  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false)

  // Auth state (localStorage'dan yukle)
  const [user, setUserState] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('user')
    if (savedUser) {
      try {
        return JSON.parse(savedUser) as User
      } catch {
        localStorage.removeItem('user')
        return null
      }
    }
    return null
  })

  const [isLoading, setIsLoading] = useState(false)

  // Tema degisikligini body'ye uygula ve localStorage'a kaydet
  // SADECE body.light class + --os-* variables — site CSS variables'larina dokunma
  useEffect(() => {
    const r = document.documentElement
    if (theme === 'light') {
      document.body.classList.add('light')
      r.style.setProperty('--os-bg', '#f0f2f7')
      r.style.setProperty('--os-surface', 'rgba(255,255,255,0.92)')
      r.style.setProperty('--os-border', 'rgba(0,0,0,0.08)')
      r.style.setProperty('--os-text', '#1a1d23')
      r.style.setProperty('--os-muted', '#6b7280')
      r.style.setProperty('--os-dim', '#9ca3af')
      r.style.setProperty('--os-mb-bg', 'rgba(255,255,255,0.88)')
      r.style.setProperty('--os-win-bg', 'rgba(255,255,255,0.95)')
      r.style.setProperty('--os-win-tb', 'rgba(248,249,250,0.9)')
      r.style.setProperty('--os-win-shadow', '0 8px 40px rgba(0,0,0,0.12)')
      r.style.setProperty('--os-dock-bg', 'rgba(255,255,255,0.75)')
      r.style.setProperty('--os-dock-pill', 'rgba(255,255,255,0.8)')
      r.style.setProperty('--os-input-bg', 'rgba(0,0,0,0.04)')
      r.style.setProperty('--os-hover', 'rgba(0,0,0,0.05)')
      r.style.setProperty('--os-desktop-bg', 'linear-gradient(135deg,#e8eaf6 0%,#f3e5f5 40%,#e3f2fd 100%)')
      r.style.setProperty('--os-icon-label', '#555')
      r.style.setProperty('--os-icon-hover', 'rgba(0,0,0,0.05)')
      r.style.setProperty('--os-bubble-user', 'rgba(99,102,241,0.9)')
      r.style.setProperty('--os-bubble-text', '#fff')
    } else {
      document.body.classList.remove('light')
      r.style.setProperty('--os-bg', '#0b0e14')
      r.style.setProperty('--os-surface', 'rgba(22,27,34,0.88)')
      r.style.setProperty('--os-border', 'rgba(255,255,255,0.06)')
      r.style.setProperty('--os-text', '#e6edf3')
      r.style.setProperty('--os-muted', '#8b949e')
      r.style.setProperty('--os-dim', '#484f58')
      r.style.setProperty('--os-mb-bg', 'rgba(0,0,0,0.55)')
      r.style.setProperty('--os-win-bg', 'rgba(22,27,34,0.88)')
      r.style.setProperty('--os-win-tb', 'rgba(0,0,0,0.25)')
      r.style.setProperty('--os-win-shadow', '0 24px 80px rgba(0,0,0,0.55)')
      r.style.setProperty('--os-dock-bg', 'rgba(0,0,0,0.35)')
      r.style.setProperty('--os-dock-pill', 'rgba(0,0,0,0.3)')
      r.style.setProperty('--os-input-bg', 'rgba(255,255,255,0.04)')
      r.style.setProperty('--os-hover', 'rgba(255,255,255,0.06)')
      r.style.setProperty('--os-desktop-bg', 'linear-gradient(135deg,#0a0a1a 0%,#1a1040 40%,#0d1a2a 100%)')
      r.style.setProperty('--os-icon-label', '#bbb')
      r.style.setProperty('--os-icon-hover', 'rgba(255,255,255,0.06)')
      r.style.setProperty('--os-bubble-user', 'rgba(99,102,241,0.88)')
      r.style.setProperty('--os-bubble-text', '#fff')
    }
    localStorage.setItem('theme', theme)
    localStorage.setItem('os-theme', theme)
  }, [theme])

  // Sidebar state'ini localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('sidebarOpen', String(sidebarOpen))
  }, [sidebarOpen])

  // Auto token refresh baslat (user varsa)
  // TEMPORARILY DISABLED - testing
  // useEffect(() => {
  //   if (user) {
  //     startAutoRefresh()
  //   }
  // }, [user])

  // Auth actions
  function setUser(newUser: User | null) {
    setUserState(newUser)
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser))
    } else {
      localStorage.removeItem('user')
    }
  }

  // Theme actions
  function toggleTheme() {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark')
  }

  // Toast actions
  function addToast(message: string, type: Toast['type'], duration = 5000) {
    const id = crypto.randomUUID()
    const toast: Toast = { id, message, type, duration }
    setToasts(prev => [...prev, toast])

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
  }

  function removeToast(id: string) {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  const isAuthenticated = user !== null

  const value: AppContextType = {
    // Auth state
    user,
    isAuthenticated,
    isLoading,
    setUser,
    setIsLoading,

    // Theme state
    theme,
    toggleTheme,

    // Sidebar state
    sidebarOpen,
    setSidebarOpen,

    // Toast state
    toasts,
    addToast,
    removeToast,

    // WebSocket state
    wsConnected,
    setWsConnected,
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useAppStore() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppStore must be used within AppProvider')
  }

  // Geriye uyumluluk icin eski API'yi de destekle
  // NOT: Dashboard state'leri simdilik bos - Dashboard component kendi state'ini yonetecek
  return {
    ...context,
    // Eski API (deprecated)
    sidebar: context.sidebarOpen,
    setSidebar: context.setSidebarOpen,
    token: localStorage.getItem('session_token'),
    login: (token: string, user: User) => {
      localStorage.setItem('session_token', token)
      context.setUser(user)
    },
    logout: () => {
      localStorage.removeItem('session_token')
      context.setUser(null)
    },
    sidebarCollapsed: !context.sidebarOpen,
    toggleSidebar: () => context.setSidebarOpen(!context.sidebarOpen),
    notifications: [],
  }
}
